import {
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, map, startWith } from 'rxjs';
import { AutoUnsubscribe } from '../../common/decorators/auto-unsubscribe.decorator';
import { Base } from '../../common/models/base.model';
import { markAsTouchedAndUpdate } from '../../common/utils';
import { IconNames } from '../../common/constants/icon-names';
import { IntrackFeatureModule } from '../../intrack-feature.module';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  selector: 'ui-select-search',
  imports: [CommonModule, IntrackFeatureModule, TranslateModule, SvgIconComponent],
  templateUrl: './select-search.component.html',
  styleUrls: ['./select-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class SelectSearchComponent implements AfterViewInit {
  @Input() selectControl: FormControl;

  @Input() set optionItems(optionItems: Base[]) {
    this.optionItems$.next(optionItems ?? []);
  }

  @Input() set isValidationTriggered(trigger: boolean) {
    if (trigger) {
      markAsTouchedAndUpdate(this.selectControl);
      this.cd.markForCheck();
    }
  }

  @Input() placeholderName: string;
  @Input() noEntriesFoundLabel: string;
  @Input() testId = 'country-select';
  @Input() showClearButton = false;
  @Input() isMultiple = false;
  @Input() tooltipText?: string;
  @Input() firstIsDefault?: boolean;
  @Input() isDisabled?: boolean;
  @Input() hintText?: string;

  private optionItems$ = new BehaviorSubject<Base[]>([]);
  iconsNames = IconNames;
  searchControl = new FormControl();
  filteredOptionItems$ = combineLatest([this.optionItems$, this.searchControl.valueChanges.pipe(startWith(''))]).pipe(
    map(([items, search]: [Base[], string]): Base[] => this.filterOptionItems(items, search || '')),
  );
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('singleSelect', { static: true }) singleSelect: MatSelect;

  constructor(private cd: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.singleSelect.compareWith = (a: unknown, b: unknown): boolean => a === b;
  }

  onClearButtonClick(event: MouseEvent): void {
    event.stopPropagation();
    this.selectControl.setValue(null);
    this.selectControl.markAsDirty();
  }

  onSearchClear(event: MouseEvent): void {
    event.stopPropagation();
    this.searchControl.setValue('');
  }

  onOpenedChange(opened: boolean): void {
    if (opened) {
      setTimeout(() => this.searchInput?.nativeElement.focus());
    }
  }

  private getSelectedIds(): string[] {
    const value = this.selectControl.value;

    if (!value) {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  }

  private filterOptionItems(items: Base[], search: string): Base[] {
    if (!search) {
      return items;
    }

    const value: string = search.toLowerCase();

    const prefix: Base[] = items.filter((item: Base): boolean => item.name.toLowerCase().startsWith(value));
    const includes: Base[] = items.filter(
      (item: Base): boolean => !item.name.toLowerCase().startsWith(value) && item.name.toLowerCase().includes(value),
    );

    const filtered: Base[] = [...prefix, ...includes];

    const selectedIds: string[] = this.getSelectedIds();
    const selectedItems: Base[] = items.filter((i: Base): boolean => selectedIds.includes(i.id));

    return [...new Map([...selectedItems, ...filtered].map((i: Base): [string, Base] => [i.id, i])).values()];
  }
}
